import {Strand} from 'constants/MolbioConstants';
import {
  CodonOptimizationMethod,
  CodonOptimizationSelectionTab,
  DEFAULT_STEM,
  DEFAULT_WINDOW,
  GCContent,
  GCLevel,
  HairpinParameter,
  OptimizeCodonsResult,
  Organism,
} from 'molbio/codon-optimization/CodonOptimizationConstants';
import {CodonOptimizationTabActionType} from 'molbio/codon-optimization/CodonOptimizationTabReduxActions';
import {BenchlingAction} from 'redux-stores/Action';

export interface ProtectedRegion {
  start: number;
  end: number;
  name: string;
}

export interface EnzymeRecognitionSite {
  enzymeName: string;
  recognitionSite: string;
  id: number;
}

export interface PatternToReduce {
  pattern: string;
  reverseComplement: boolean;
}

export const DEFAULT_PATTERN_TO_REDUCE: PatternToReduce = {pattern: '', reverseComplement: false};

export interface CodonOptimizationTabState {
  selectedOrganism: Organism | null;
  selectedGCLevel: GCLevel;
  isShowingOptions: boolean;
  isLoading: boolean;
  error: string | null;
  result: OptimizeCodonsResult | null;
  optimizationStart: number | null;
  optimizationEnd: number | null;
  bases: string;
  protectedRegions: Array<ProtectedRegion>;
  isOptimizationEntireSequence: boolean | null;
  // This is used for back translation from an AA sequence: cutsites are avoided throughout entire sequence.
  cutsitesToAvoid: Array<EnzymeRecognitionSite>;
  strand: Strand | null;
  // This is used for codon optimizing a DNA sequence: cutsites are avoided wherever there do not already
  // exist match sites.
  cutsitesToPreserve: Array<EnzymeRecognitionSite>;
  uridineDepletion: boolean;
  avoidHairpins: boolean;
  hairpinStem: HairpinParameter;
  hairpinWindow: HairpinParameter;
  activeTab: CodonOptimizationSelectionTab;
  patternsToReduce: Array<PatternToReduce>;
  method: CodonOptimizationMethod;
  gcContent: GCContent;
}

export const INITIAL_STATE: CodonOptimizationTabState = {
  selectedOrganism: null,
  selectedGCLevel: GCLevel.ANY,
  isShowingOptions: true,
  isLoading: false,
  error: null,
  result: null,
  optimizationStart: null,
  optimizationEnd: null,
  bases: '',
  protectedRegions: [],
  isOptimizationEntireSequence: null,
  cutsitesToAvoid: [],
  strand: null,
  cutsitesToPreserve: [],
  uridineDepletion: false,
  avoidHairpins: false,
  hairpinStem: {value: DEFAULT_STEM, error: ''},
  hairpinWindow: {value: DEFAULT_WINDOW, error: ''},
  activeTab: CodonOptimizationSelectionTab.AVOIDED_CUTSITES,
  patternsToReduce: [],
  method: CodonOptimizationMethod.MATCH_CODON_USAGE,
  gcContent: {min: 0, max: 1},
};

export default function reduceCodonOptimizationTab(
  state: CodonOptimizationTabState = INITIAL_STATE,
  action: BenchlingAction
): CodonOptimizationTabState {
  const isEqualProtectedRegion = (region1: ProtectedRegion, region2: ProtectedRegion): boolean =>
    region1.name === region2.name && region1.start === region2.start && region1.end === region2.end;
  const newPatternsToReduce = state.patternsToReduce;
  switch (action.type) {
    case CodonOptimizationTabActionType.UPDATE_SELECTED_ORGANISM:
      return {...state, selectedOrganism: action.selectedOrganism, error: null};
    case CodonOptimizationTabActionType.UPDATE_SELECTED_GC_LEVEL:
      return {...state, selectedGCLevel: action.selectedGCLevel, error: null};
    case CodonOptimizationTabActionType.PREVIEW_PENDING:
      return {...state, isLoading: true};
    case CodonOptimizationTabActionType.PREVIEW_COMPLETED:
      return {...state, result: action.result, isLoading: false, isShowingOptions: false};
    case CodonOptimizationTabActionType.PREVIEW_ERROR:
      return {...state, error: action.error, isLoading: false};
    case CodonOptimizationTabActionType.SAVE_OPTIMIZATION_COMPLETED:
    case CodonOptimizationTabActionType.CLOSE_OPTIMIZATION:
      return INITIAL_STATE;
    case CodonOptimizationTabActionType.GO_BACK:
      return {...state, isShowingOptions: true};
    case CodonOptimizationTabActionType.SET_SELECTION:
      return {
        ...state,
        optimizationStart: action.start,
        optimizationEnd: action.end,
        bases: action.bases,
        isOptimizationEntireSequence: action.isOptimizationEntireSequence,
        strand: action.strand,
      };
    case CodonOptimizationTabActionType.ADD_PROTECTED_REGION:
      return {...state, protectedRegions: state.protectedRegions.concat(action.region), error: null};
    case CodonOptimizationTabActionType.DELETE_PROTECTED_REGION:
      return {
        ...state,
        error: null,
        protectedRegions: state.protectedRegions.filter(
          (region) => !isEqualProtectedRegion(region, action.region)
        ),
      };
    case CodonOptimizationTabActionType.DELETE_ALL_PROTECTED_REGIONS:
      return {
        ...state,
        protectedRegions: [],
      };
    case CodonOptimizationTabActionType.IS_INVALID_PROTECTED_REGION:
      return {
        ...state,
        error: action.message,
      };
    case CodonOptimizationTabActionType.ADD_CUTSITE_TO_AVOID:
      return {
        ...state,
        cutsitesToAvoid: [...state.cutsitesToAvoid, action.cutsite],
      };
    case CodonOptimizationTabActionType.DELETE_CUTSITE_TO_AVOID:
      return {
        ...state,
        cutsitesToAvoid: state.cutsitesToAvoid.filter((cutsite) => cutsite.id !== action.enzymeId),
      };
    case CodonOptimizationTabActionType.DELETE_ALL_CUTSITES_TO_AVOID:
      return {
        ...state,
        cutsitesToAvoid: [],
      };
    case CodonOptimizationTabActionType.ADD_CUTSITE_TO_PRESERVE:
      return {
        ...state,
        cutsitesToPreserve: [...state.cutsitesToPreserve, action.cutsite],
      };
    case CodonOptimizationTabActionType.DELETE_CUTSITE_TO_PRESERVE:
      return {
        ...state,
        cutsitesToPreserve: state.cutsitesToPreserve.filter((cutsite) => cutsite.id !== action.enzymeId),
      };
    case CodonOptimizationTabActionType.DELETE_ALL_CUTSITES_TO_PRESERVE:
      return {
        ...state,
        cutsitesToPreserve: [],
      };
    case CodonOptimizationTabActionType.UPDATE_URIDINE_DEPLETION:
      return {
        ...state,
        uridineDepletion: action.uridineDepletion,
      };
    case CodonOptimizationTabActionType.UPDATE_HAIRPIN_PARAMETERS:
      return {
        ...state,
        avoidHairpins: action.avoidHairpins,
        hairpinStem: action.hairpinStem,
        hairpinWindow: action.hairpinWindow,
      };
    case CodonOptimizationTabActionType.UPDATE_ACTIVE_TAB:
      return {
        ...state,
        activeTab: action.tab,
      };
    case CodonOptimizationTabActionType.ADD_PATTERN_TO_REDUCE:
      return {
        ...state,
        patternsToReduce: [...state.patternsToReduce, DEFAULT_PATTERN_TO_REDUCE],
      };
    case CodonOptimizationTabActionType.DELETE_ALL_PATTERNS_TO_REDUCE:
      return {
        ...state,
        patternsToReduce: [],
      };
    case CodonOptimizationTabActionType.DELETE_PATTERN_TO_REDUCE:
      return {
        ...state,
        patternsToReduce: state.patternsToReduce.filter((patternToReduce, i) => i !== action.patternIdx),
      };
    case CodonOptimizationTabActionType.UPDATE_PATTERN_TO_REDUCE:
      newPatternsToReduce[action.patternIdx] = action.patternToReduce;
      return {
        ...state,
        patternsToReduce: newPatternsToReduce,
      };
    case CodonOptimizationTabActionType.UPDATE_CUSTOM_GC_CONTENT:
      return {...state, gcContent: action.gcContent, error: null};
    case CodonOptimizationTabActionType.UPDATE_OPTIMIZATION_METHOD:
      return {...state, method: action.method, error: null};
    default:
      return state;
  }
}
